import React, { useEffect, useMemo, useState } from "react";
import { FaInfoCircle, FaMinus, FaPlus } from "react-icons/fa";
import { toast } from "react-toastify";
import formatDate from "./../../../utils/formatDate";
import {
  getAdherentsByEvent,
  getAdherentsCountByEvent,
  getAdherentResults,
} from "./../../../services/eventServices";
import getCategory from "../../../utils/getCategory";
import getWeightCategory from "../../../utils/getWeightCategory";
import EventDetailsModalEnseignants from "./../../PagesEnseignants/EventsPage/EventDetailsModalEnseignants";
import RegisterAdherentModal from "./RegisterAdherentModal";
import UnregisterAdherentModal from "./UnregisterAdherentModal";
import EditResultCompetitorEvent from "./EditResultCompetitorEvent";

const getDefaultPhoto = (genre) =>
  genre === "Masculin" ? "/img/judoka_avatar.png" : "/img/judokate_avatar.png";

const AdherentTag = ({ adherent, enseignants }) => {
  const isCoach = (enseignants || []).some((e) => e.id === adherent.id);
  if (isCoach) {
    return (
      <div className="absolute top-0 left-0 bg-red-500 text-white px-2 py-1 rounded-br-lg text-[11px]">
        Coach
      </div>
    );
  }
  const ageCategory = getCategory(adherent.date_naissance);
  const weightCategory = getWeightCategory(
    adherent.genre,
    ageCategory,
    adherent.poids
  );
  return (
    <div className="absolute top-0 left-0 bg-blue-600 text-white px-2 py-1 rounded-br-lg text-[11px]">
      {ageCategory} | {weightCategory}
    </div>
  );
};

const EventCard = ({ event, enseignants, onCountUpdate }) => {
  const [expanded, setExpanded] = useState(false);
  const [adherents, setAdherents] = useState(null); // null = pas chargé, [] = chargé vide
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(undefined);

  // Modales locales
  const [showInfo, setShowInfo] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showUnregister, setShowUnregister] = useState(false);

  const [resultsOpen, setResultsOpen] = useState(false);
  const [resultsData, setResultsData] = useState(null); // { event, adherentDetails, results }

  // Compte rapide des inscrits (léger)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const c = await getAdherentsCountByEvent(event.id);
        if (!mounted) return;
        setCount(typeof c === "number" ? c : Number(c?.count || 0));
        onCountUpdate?.(
          event.id,
          typeof c === "number" ? c : Number(c?.count || 0)
        );
      } catch {
        // silencieux
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.id]);

  // Charge les inscrits au premier dépliage uniquement
  const ensureAdherentsLoaded = async () => {
    if (adherents !== null) return;
    try {
      setLoading(true);
      const list = await getAdherentsByEvent(event.id);
      setAdherents(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors du chargement des inscrits.");
      setAdherents([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next) await ensureAdherentsLoaded();
  };

  const handleOpenResults = async (adherent) => {
    try {
      const results = await getAdherentResults(adherent.id, event.id);
      const ageCategory = getCategory(adherent.date_naissance);
      const weightCategory = getWeightCategory(
        adherent.genre,
        ageCategory,
        adherent.poids
      );

      setResultsData({
        event: { id: event.id, titre: event.titre, start: event.start },
        adherentDetails: {
          ...adherent,
          ageCategory,
          weightCategory,
          isCoach: (enseignants || []).some((x) => x.id === adherent.id),
        },
        results: results || {},
      });
      setResultsOpen(true);
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors du chargement des résultats.");
    }
  };

  // Après inscription / désinscription on refresh la liste + le compteur
  const refreshAfterChange = async () => {
    try {
      const [list, c] = await Promise.all([
        getAdherentsByEvent(event.id),
        getAdherentsCountByEvent(event.id),
      ]);
      setAdherents(Array.isArray(list) ? list : []);
      const nextCount = typeof c === "number" ? c : Number(c?.count || 0);
      setCount(nextCount);
      onCountUpdate?.(event.id, nextCount);
    } catch {}
  };

  const dateLabel = useMemo(() => formatDate(event.start), [event.start]);
  const hasRegistrations = typeof count === "number" && count > 0;

  const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    // déjà absolu, data URL ou blob
    if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) return s;
    // chemins d'assets front (ex: /img/...): on laisse tel quel
    if (!s.startsWith("/uploads")) return s;
    // fichiers uploadés côté API
    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };

  return (
    <article className="rounded-2xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-800 bg-white/90 dark:bg-slate-900/60">
      {/* Header compact */}
      <div className="flex items-center justify-between px-4 sm:px-5 py-3 bg-slate-800 text-white">
        <div className="min-w-0">
          <h3 className="text-base sm:text-lg font-semibold truncate">
            {event.titre || "Événement"}
          </h3>
          <div className="text-xs opacity-90">
            {dateLabel} • {event.type_event}{" "}
            {event.level_event ? `• ${event.level_event}` : ""}
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <span
            className={`text-xs px-2 py-0.5 rounded-full ${
              hasRegistrations
                ? "bg-red-600 text-white"
                : "bg-white/15 text-slate-900 dark:text-slate-100"
            }`}
          >
            {typeof count === "number"
              ? `${count} inscrit${count > 1 ? "s" : ""}`
              : "…"}
          </span>

          <button
            type="button"
            onClick={() => setShowInfo(true)}
            className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-white/20 hover:bg-white/30 transition"
            title="Détails de l’événement"
          >
            <FaInfoCircle />
          </button>

          <button
            type="button"
            onClick={toggleExpand}
            className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-white text-slate-900 hover:bg-slate-100 transition"
            title={expanded ? "Replier" : "Déplier"}
          >
            {expanded ? "▲" : "▼"}
          </button>
        </div>
      </div>

      {/* Corps — liste des inscrits (lazy) */}
      {expanded && (
        <div className="px-4 sm:px-5 py-4">
          {/* Actions rapides */}
          <div className="mb-4 flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setShowRegister(true)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white dark:bg-slate-900 ring-1 ring-slate-300 dark:ring-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              <FaPlus /> Inscrire un adhérent
            </button>

            {(adherents || []).length > 0 && (
              <button
                type="button"
                onClick={() => setShowUnregister(true)}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white dark:bg-slate-900 ring-1 ring-slate-300 dark:ring-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                <FaMinus /> Désinscrire un adhérent
              </button>
            )}
          </div>

          {/* Grille des inscrits */}
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className="h-44 rounded-xl border border-slate-200 dark:border-slate-800 bg-white/60 dark:bg-slate-900/40 animate-pulse"
                />
              ))}
            </div>
          ) : (adherents || []).length === 0 ? (
            <div className="text-sm opacity-75">
              Aucun inscrit pour le moment.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {adherents.map((ad) => (
                <div
                  key={ad.id}
                  className="relative rounded-lg overflow-hidden ring-1 ring-slate-200 dark:ring-slate-800 bg-white dark:bg-slate-900 cursor-pointer group"
                  title="Voir / saisir le résultat"
                  onClick={() => handleOpenResults(ad)}
                >
                  <img
                    src={toAbsolute(ad.photo_url) || getDefaultPhoto(ad.genre)}
                    alt={`${ad.nom} ${ad.prenom}`}
                    className="block w-full h-40 object-cover"
                    draggable={false}
                  />
                  <AdherentTag adherent={ad} enseignants={enseignants} />
                  <div className="px-2 py-1 text-center">
                    <p
                      className="text-[13px] font-semibold leading-snug text-slate-900 dark:text-slate-100"
                      style={{
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 2,
                        overflow: "hidden",
                      }}
                      title={`${(ad.nom || "").toUpperCase()} ${
                        ad.prenom || ""
                      }`}
                    >
                      {(ad.nom || "").toUpperCase()} {ad.prenom || ""}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modales locales */}
      {showInfo && (
        <EventDetailsModalEnseignants
          isOpen={showInfo}
          onClose={() => setShowInfo(false)}
          event={event}
          adherents={adherents || []}
        />
      )}

      {showRegister && (
        <RegisterAdherentModal
          isOpen={showRegister}
          onClose={() => setShowRegister(false)}
          event={event}
          onChanged={refreshAfterChange}
        />
      )}

      {showUnregister && (
        <UnregisterAdherentModal
          isOpen={showUnregister}
          onClose={() => setShowUnregister(false)}
          event={event}
          adherents={adherents || []}
          onChanged={refreshAfterChange}
        />
      )}

      {resultsOpen && resultsData && (
        <EditResultCompetitorEvent
          isOpen={resultsOpen}
          onClose={() => setResultsOpen(false)}
          event={resultsData.event}
          adherentDetails={resultsData.adherentDetails}
          results={resultsData.results}
        />
      )}
    </article>
  );
};

export default EventCard;
